import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './app';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { APP_BUILD_ID, cleanReloadParamFromUrl, reloadApp } from '@/lib/app-update';
import { installClientErrorReporting } from '@/lib/client-errors';
import { startLaunchCache } from '@/lib/launch-cache';
import { registerAppServiceWorker } from '@/lib/app-service-worker';
import { prefCodec, readPref, UI_PREFS, withCodec, writePref } from '@/lib/ui-prefs';
import { useAuthStore } from '@/stores/authStore';
import './styles/global.css';

cleanReloadParamFromUrl();

// T-68: the last Inbox and projects list, shown at once while they load again; and the service
// worker that keeps the app itself on this device, so it opens without waiting for the network.
startLaunchCache();
registerAppServiceWorker();

const PRELOAD_RELOAD_AT = withCodec(UI_PREFS.appPreloadReloadAt, prefCodec.text);

// After a deploy, an open tab can ask for a page file that no longer exists. Reload once to
// pick up the new build (at most once a minute, so a real outage doesn't loop).
window.addEventListener('vite:preloadError', (event) => {
  const last = Number(readPref(PRELOAD_RELOAD_AT) || 0);
  if (Date.now() - last < 60_000) return;
  writePref(PRELOAD_RELOAD_AT, String(Date.now()));
  event.preventDefault();
  reloadApp();
});

const container = document.getElementById('root');
if (!container) {
  throw new Error('Failed to find the root element');
}

// Every crash a boundary catches, and every unhandled error, is reported once from here.
const root = createRoot(
  container,
  installClientErrorReporting({
    source: 'app',
    endpoint: '/api/client-errors',
    buildId: APP_BUILD_ID,
    // The token only says who it was in the log; the route accepts reports without one.
    headers: (): Record<string, string> => {
      const token = useAuthStore.getState().user?.token;
      // eslint-disable-next-line no-restricted-syntax -- the reporter calls fetch, not fetchApi, on purpose (lib/client-errors.ts).
      return token ? { Authorization: `Bearer ${token}` } : {};
    },
  })
);
root.render(
  <React.StrictMode>
    {/* Last resort: a crash in the app shell itself (layouts, providers) leaves a way out. */}
    <ErrorBoundary variant="root">
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
